import { ref, computed } from 'vue';
import pendingQuestionsAPI from '../api/omni/pendingQuestions';
import { useAlert } from 'dashboard/composables';

export function usePendingQuestions() {
  const pendingQuestions = ref([]);
  const bots = ref([]);
  const loading = ref(false);
  const stats = ref({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
  });
  const meta = ref({
    page: 1,
    per_page: 25,
    total: 0,
  });

  const totalPages = computed(() => {
    return Math.ceil(meta.value.total / meta.value.per_page);
  });

  const hasQuestions = computed(() => pendingQuestions.value.length > 0);

  const fetchQuestions = async (params = {}) => {
    loading.value = true;
    try {
      const response = await pendingQuestionsAPI.get(params);
      pendingQuestions.value = response.data.pending_questions || [];
      meta.value = response.data.meta || meta.value;
      stats.value = {
        total: response.data.meta?.total || 0,
        pending: response.data.meta?.pending_count || 0,
        approved: response.data.meta?.approved_count || 0,
        rejected: response.data.meta?.rejected_count || 0,
      };
    } catch (error) {
      useAlert(error.message || 'Error fetching pending questions');
    } finally {
      loading.value = false;
    }
  };

  const fetchBots = async () => {
    try {
      const response = await pendingQuestionsAPI.getBots();
      bots.value = response.data || [];
    } catch (error) {
      useAlert(error.message || 'Error fetching bots');
    }
  };

  const fetchStats = async (params = {}) => {
    try {
      const response = await pendingQuestionsAPI.getStats(params);
      stats.value = response.data;
    } catch (error) {
      useAlert(error.message || 'Error fetching stats');
    }
  };

  const createQuestion = async (questionData) => {
    loading.value = true;
    try {
      const response = await pendingQuestionsAPI.create(questionData);
      pendingQuestions.value.unshift(response.data);
      useAlert('Question added successfully', 'success');
      return response.data;
    } catch (error) {
      if (error.response?.data?.already_rejected) {
        useAlert('Similar question was already rejected', 'warning');
      } else {
        useAlert(error.message || 'Error creating question');
      }
      throw error;
    } finally {
      loading.value = false;
    }
  };

  const updateQuestion = async (id, questionData) => {
    loading.value = true;
    try {
      const response = await pendingQuestionsAPI.update(id, questionData);
      const index = pendingQuestions.value.findIndex(q => q.id === id);
      if (index !== -1) {
        pendingQuestions.value[index] = response.data;
      }
      useAlert('Question updated successfully', 'success');
      return response.data;
    } catch (error) {
      useAlert(error.message || 'Error updating question');
      throw error;
    } finally {
      loading.value = false;
    }
  };

  const deleteQuestion = async (id) => {
    loading.value = true;
    try {
      await pendingQuestionsAPI.delete(id);
      pendingQuestions.value = pendingQuestions.value.filter(q => q.id !== id);
      useAlert('Question deleted successfully', 'success');
    } catch (error) {
      useAlert(error.message || 'Error deleting question');
      throw error;
    } finally {
      loading.value = false;
    }
  };

  const approveQuestion = async (id, answer) => {
    loading.value = true;
    try {
      const response = await pendingQuestionsAPI.approve(id, answer);
      const index = pendingQuestions.value.findIndex(q => q.id === id);
      if (index !== -1) {
        pendingQuestions.value[index] = response.data.pending_question;
      }
      useAlert('Question approved and added to knowledge base!', 'success');
      return response.data;
    } catch (error) {
      useAlert(error.message || 'Error approving question');
      throw error;
    } finally {
      loading.value = false;
    }
  };

  const rejectQuestion = async (id, reason = null) => {
    loading.value = true;
    try {
      const response = await pendingQuestionsAPI.reject(id, reason);
      const index = pendingQuestions.value.findIndex(q => q.id === id);
      if (index !== -1) {
        pendingQuestions.value[index] = response.data.pending_question;
      }
      useAlert('Question rejected', 'success');
      return response.data;
    } catch (error) {
      useAlert(error.message || 'Error rejecting question');
      throw error;
    } finally {
      loading.value = false;
    }
  };

  const changePage = async (page, additionalParams = {}) => {
    await fetchQuestions({ page, ...additionalParams });
  };

  return {
    pendingQuestions,
    bots,
    loading,
    stats,
    meta,
    totalPages,
    hasQuestions,
    fetchQuestions,
    fetchBots,
    fetchStats,
    createQuestion,
    updateQuestion,
    deleteQuestion,
    approveQuestion,
    rejectQuestion,
    changePage,
  };
}

